import React from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  TablePagination,
} from '@mui/material'
import StarRating from './StarRating'

const UI = {
  COLORS: {
    headerBg: '#f7f7f9',
    rowHover: 'rgba(0,89,246,0.03)',
    border: '#e7edf6',
    ink: '#06185f',
    muted: '#4c5d86',
    blue: '#0059f6',
  },
  WIDTHS: {
    nombre: 100,
    consulta: 100,
    estadioCierre: 80,
    puntuacion: 100,
    fecha: 100,
    vendedor: 100,
    acciones: 80,
    estado: 110,
  },
  ESTADOS: ['ACTIVO', 'VENTA', 'BAJA', 'FUTURO'],
}

const STATUS_STYLES = {
  ACTIVO: { solid: '#0059f6', bg: 'rgba(0, 89, 246, 0.10)', text: '#06185f' },
  VENTA: { solid: '#96c87c', bg: 'rgba(150, 200, 124, 0.18)', text: '#2f8f3a' },
  BAJA: { solid: '#fd8d82', bg: 'rgba(253, 141, 130, 0.18)', text: '#e34d43' },
  FUTURO: { solid: '#b1b5c2', bg: 'rgba(177, 181, 194, 0.20)', text: '#7d8495' },
}

const cellBase = {
  fontSize: '12px',
  borderColor: UI.COLORS.border,
  py: 1.25,
  px: 1,
  color: UI.COLORS.ink,
  textAlign: 'center',
}

const headerCellBase = {
  ...cellBase,
  py: 1.35,
  fontWeight: 900,
  color: UI.COLORS.ink,
}

function formatFecha(fechaStr) {
  if (!fechaStr) return '-'
  const d = new Date(fechaStr)
  if (isNaN(d)) return '-'
  const pad = (n) => String(n).padStart(2, '0')
  const dd = pad(d.getDate())
  const mm = pad(d.getMonth() + 1)
  const aa = String(d.getFullYear()).slice(-2)
  const hh = pad(d.getHours())
  const min = pad(d.getMinutes())
  return `${dd}/${mm}/${aa} ${hh}:${min}`
}

const getStatusStyle = (estado) => {
  return STATUS_STYLES[estado] || STATUS_STYLES.ACTIVO
}

/**
 * Props:
 * - rows: array con los items de consultas cerradas
 * - onOpenDevoluciones(row)
 * - onOpenChecklist(row)
 * - onEstadoChange(row, newEstado)
 */
export default function ConsultasCerradasTable({
  rows = [],
  onOpenSeguimiento,
  onEstadoChange,
  onOpenReassign,
  count = 0,
  page = 0,
  rowsPerPage = 15,
  onPageChange,
  customHeight,
}) {
  const isFlexMode = customHeight === "flex";

  return (
    <Box sx={{
      width: '100%',
      overflow: 'hidden',
      display: isFlexMode ? 'flex' : 'block',
      flexDirection: 'column',
      flex: isFlexMode ? 1 : 'none',
      height: isFlexMode ? '100%' : 'auto',
      minHeight: 0,
      backgroundColor: 'rgba(255,255,255,0.96)',
      border: '1px solid #dce5f2',
      borderRadius: '16px',
      boxShadow: '0 20px 44px rgba(19, 39, 84, 0.11)',
      boxSizing: 'border-box',
    }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: '16px 16px 8px 8px',
          border: `1px solid ${UI.COLORS.border}`,
          flex: isFlexMode ? 1 : 'none',
          height: isFlexMode ? '100%' : 'auto',
          minHeight: 0,
          overflowY: 'auto',
          maxHeight: isFlexMode ? 'none' : "56vh",
          backgroundColor: '#fff',
          transition: "flex 0.3s ease"
        }}
      >
        <Table size="small" aria-label="consultas cerradas" sx={{ tableLayout: 'fixed' }} stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { borderBottom: `1px solid ${UI.COLORS.border}` } }}>
              <TableCell sx={{ ...headerCellBase, width: UI.WIDTHS.nombre, backgroundColor: UI.COLORS.headerBg, borderRight: `1px solid ${UI.COLORS.border}` }}>
                Nombre
              </TableCell>
              <TableCell sx={{ ...headerCellBase, width: UI.WIDTHS.consulta, backgroundColor: UI.COLORS.headerBg, borderRight: `1px solid ${UI.COLORS.border}` }}>
                Consulta
              </TableCell>
              <TableCell sx={{ ...headerCellBase, width: UI.WIDTHS.estadioCierre, backgroundColor: UI.COLORS.headerBg, borderRight: `1px solid ${UI.COLORS.border}` }}>
                Estadio cierre
              </TableCell>
              <TableCell align="center" sx={{ ...headerCellBase, width: UI.WIDTHS.fecha, backgroundColor: UI.COLORS.headerBg, borderRight: `1px solid ${UI.COLORS.border}` }}>
                Fecha carga
              </TableCell>
              <TableCell align="center" sx={{ ...headerCellBase, width: UI.WIDTHS.fecha, backgroundColor: UI.COLORS.headerBg, borderRight: `1px solid ${UI.COLORS.border}` }}>
                Inicio asignación
              </TableCell>
              <TableCell align="center" sx={{ ...headerCellBase, width: UI.WIDTHS.fecha, backgroundColor: UI.COLORS.headerBg, borderRight: `1px solid ${UI.COLORS.border}` }}>
                Cierre asignación
              </TableCell>
              <TableCell align="center" sx={{ ...headerCellBase, width: UI.WIDTHS.puntuacion, backgroundColor: UI.COLORS.headerBg, borderRight: `1px solid ${UI.COLORS.border}` }}>
                Puntuación
              </TableCell>
              <TableCell sx={{ ...headerCellBase, width: UI.WIDTHS.vendedor, backgroundColor: UI.COLORS.headerBg, borderRight: `1px solid ${UI.COLORS.border}` }}>
                Vendedor
              </TableCell>
              <TableCell align="center" sx={{ ...headerCellBase, width: UI.WIDTHS.acciones, backgroundColor: UI.COLORS.headerBg, borderRight: `1px solid ${UI.COLORS.border}` }}>
                Seguimiento
              </TableCell>
              <TableCell align="center" sx={{ ...headerCellBase, width: UI.WIDTHS.estado, backgroundColor: UI.COLORS.headerBg }}>
                Estado asignación
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} sx={{ py: 6, borderBottom: `1px solid ${UI.COLORS.border}` }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ opacity: 0.8 }}>
                      No hay consultas cerradas para los filtros seleccionados
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                      Ajustá los filtros para ver otros resultados.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {rows.map((r) => {
              const statusStyle = getStatusStyle(r?.estado)
              const id = r?.id_ven_cli ?? r?.id
              return (
                <TableRow
                  key={id}
                  sx={{
                    transition: 'background-color 120ms ease, opacity 120ms ease',
                    '&:hover': { backgroundColor: UI.COLORS.rowHover },
                    backgroundColor: '#fff',
                    '& td': { borderBottom: `1px solid ${UI.COLORS.border}` },
                  }}
                >
                  <TableCell
                    sx={{
                      ...cellBase,
                      position: 'relative',
                      pl: 1.4,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 5,
                        backgroundColor: statusStyle.solid,
                      },
                    }}
                    title={r?.cliente_nombre ?? '-'}
                  >
                    {r?.cliente_nombre ?? '-'}
                  </TableCell>
                  <TableCell sx={{ ...cellBase }} title={r?.consulta ?? '-'}>
                    {r?.consulta ?? '-'}
                  </TableCell>
                  <TableCell sx={{ ...cellBase }} title={r?.estadio_cierre ?? '-'}>
                    {r?.estadio_cierre ?? '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ ...cellBase }}>
                    {formatFecha(r?.fecha_carga_cliente)}
                  </TableCell>
                  <TableCell align="center" sx={{ ...cellBase }}>
                    {formatFecha(r?.fecha_inicio_asignacion)}
                  </TableCell>
                  <TableCell align="center" sx={{ ...cellBase }}>
                    {formatFecha(r?.fecha_cierre_asignacion)}
                  </TableCell>
                  <TableCell align="center" sx={{ ...cellBase }}>
                    {Number(r?.puntuacion_venta) > 0 ? (
                      <Tooltip title={`${r.puntuacion_venta}/10`} arrow>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                          <StarRating
                            value={Math.max(0, Math.min(5, Number(r.puntuacion_venta) / 2))}
                            readOnly
                            size={18}
                            precision={0.5}
                            gap={0.1}
                            aria-label="puntuación cliente"
                          />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" sx={{ opacity: 0.75 }}>
                        Sin valoración
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell
                    sx={{ ...cellBase }}
                    title={r?.vendedor_nombre ?? '-'}
                  >
                    {(() => {
                      const isInactive = (r?.vendedor_activo === false || r?.vendedor_activo === 0);
                      const tooltipText = isInactive ? 'Vendedor inactivo - requiere reasignación' : (onOpenReassign ? 'doble click para reasignar' : '');
                      return (
                        <Tooltip title={tooltipText} arrow disableHoverListener={!onOpenReassign && !isInactive}>
                          <Box
                            onDoubleClick={() => onOpenReassign && onOpenReassign(r)}
                            tabIndex={onOpenReassign ? 0 : -1}
                            role={onOpenReassign ? 'button' : undefined}
                            onKeyDown={(e) => {
                              if (!onOpenReassign) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onOpenReassign(r);
                              }
                            }}
                            sx={{
                              display: 'inline-block',
                              px: 1,
                              py: 0.45,
                              border: onOpenReassign ? `1px solid ${UI.COLORS.border}` : 'none',
                              borderRadius: '6px',
                              cursor: onOpenReassign ? 'pointer' : 'default',
                              backgroundColor: onOpenReassign ? '#f6f7f9' : 'transparent',
                              color: isInactive ? 'error.main' : 'inherit',
                              textDecoration: isInactive ? 'line-through' : 'none',
                              fontWeight: 700,
                            }}
                          >
                            {r?.vendedor_nombre ?? '-'}
                          </Box>
                        </Tooltip>
                      )
                    })()}
                  </TableCell>

                  <TableCell align="center" sx={{ ...cellBase }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onOpenSeguimiento?.(r)}
                      sx={{
                        minWidth: 74,
                        borderRadius: '8px',
                        color: UI.COLORS.blue,
                        borderColor: 'rgba(0,89,246,0.55)',
                        fontSize: 12,
                        fontWeight: 800,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: UI.COLORS.blue,
                          backgroundColor: 'rgba(0,89,246,0.06)',
                        },
                      }}
                    >
                      Ver más
                    </Button>
                  </TableCell>

                  <TableCell align="center" sx={{ ...cellBase }}>
                    <Select
                      size="small"
                      value={r?.estado ?? 'VENTA'}
                      onChange={(e) => onEstadoChange?.(r, e.target.value)}
                      displayEmpty
                      renderValue={(val) => (
                        <Box
                          component="span"
                          sx={{
                            color: getStatusStyle(val).text,
                            fontSize: 12,
                            fontWeight: 900,
                            lineHeight: 1,
                          }}
                        >
                          {val}
                        </Box>
                      )}
                      sx={{
                        width: '100%',
                        minHeight: 34,
                        borderRadius: '8px',
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.text,
                        fontWeight: 900,
                        '& .MuiSelect-select': {
                          py: 0.45,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pr: '30px !important',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: statusStyle.solid,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: statusStyle.solid,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: statusStyle.solid,
                        },
                        '& .MuiSvgIcon-root': {
                          color: statusStyle.text,
                          right: 8,
                        },
                      }}
                    >
                      {UI.ESTADOS.map((e) => (
                        <MenuItem key={e} value={e}>
                          <Chip
                            size="small"
                            label={e}
                            sx={{
                              minWidth: 72,
                              backgroundColor: getStatusStyle(e).bg,
                              color: getStatusStyle(e).text,
                              border: `1px solid ${getStatusStyle(e).solid}`,
                              fontWeight: 900,
                            }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* 🔹 Paginación */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: 'background.paper',
          border: `1px solid ${UI.COLORS.border}`,
          borderTop: 0,
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
        }}
      >
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
        />
      </Box>
    </Box>
  )
}
